import { stepStudentMedicalBenefitsTugoLookup as expenseLookup } from './claimCodeMapper';
import { MEDICAL_EXPENSE_TYPES } from '../Tugo.StudentMedicalBenefits/constants';

function mapToParentSection() {
  return {};
}

function mapToQuestionnaire() {
  return [];
}

// Generic function to map any type of expense
function mapExpensesByType(data, itemsKey, expenseLookupKey, condition = true) {
  if (!condition || !data[itemsKey] || data[itemsKey].length < 1) {
    return [];
  }

  // Check if lookup key exists in expense lookup
  if (!Object.prototype.hasOwnProperty.call(expenseLookup, expenseLookupKey)) {
    return [];
  }

  // Get claim code from associatedClaimCodes (same pattern as getClaimCodeFromLookup)
  const lookup = expenseLookup[expenseLookupKey];
  const claimCode = lookup && lookup.associatedClaimCodes && lookup.associatedClaimCodes.length > 0
    ? lookup.associatedClaimCodes[0]
    : (lookup.claimCode || null);
  
  if (!claimCode) {
    return [];
  }

  const expenses = [];

  data[itemsKey].forEach((item) => {
    const expense = mapExpenseItem(item, data, claimCode);
    if (!!expense) {
      expenses.push(expense);
    }
  });

  return expenses;
}

function mapToExpenses(data) {
  const expensesSelected = !!data.whatExpensesAreYouClaimingFor && typeof data.whatExpensesAreYouClaimingFor.toJS === 'function'
    ? data.whatExpensesAreYouClaimingFor.toJS()
    : data.whatExpensesAreYouClaimingFor || [];

  const expenseConfigs = [
    // Doctor's Visit expenses
    {
      items: 'infoAnnualPhysicalExamItems',
      lookup: 'annualPhysicalExam',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitAnnualPhysicalExam === 'true',
    },
    {
      items: 'infoOutpatientPhysicianFeesItems',
      lookup: 'outpatientPhysicianFees',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFirstVisit === 'true',
    },
    {
      items: 'infoFollowUpVisitItems',
      lookup: 'followUpVisit',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUp === 'true',
    },
    {
      items: 'infoFollowUpFractureItems',
      lookup: 'fractureTreatment',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUpFracture === 'true',
    },
    {
      items: 'infoSTITestingItems',
      lookup: 'stiTesting',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitSTIVaccinationsTBEyeExam === 'true' && data.doctorVisitSTITesting === true,
    },
    {
      items: 'infoVaccinationsTBTestingItems',
      lookup: 'vaccinationsTBTesting',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitSTIVaccinationsTBEyeExam === 'true' && data.doctorVisitVaccinationsTBTesting === true,
    },
    {
      items: 'infoEyeExaminationItems',
      lookup: 'eyeExamination',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitSTIVaccinationsTBEyeExam === 'true' && data.doctorVisitEyeExamination === true,
    },

    // Diagnostic Tests expenses
    {
      items: 'infoDiagnosticTestsItems',
      lookup: 'diagnosticTests',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS),
    },

    // Prescription Drugs expenses
    {
      items: 'infoPrescriptionDrugsItems',
      lookup: 'prescriptionDrugs',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS),
    },

    // Ambulance Services expenses
    {
      items: 'infoGroundAmbulanceItems',
      lookup: 'groundAmbulance',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceGroundService === 'true',
    },
    {
      items: 'infoAirAmbulanceItems',
      lookup: 'airAmbulance',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceAirService === 'true',
    },
    {
      items: 'infoGroundAmbulanceItems',
      lookup: 'groundAmbulance',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceAirService === 'false',
    },

    // Hospitalization expenses
    {
      items: 'infoHospitalFeesItems',
      lookup: 'hospitalFees',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationHospitalInvoice === true,
    },
    {
      items: 'infoInpatientPhysicianFeesItems',
      lookup: 'inpatientPhysicianFees',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationPhysicianInvoice === true,
    },
    {
      items: 'infoXrayLabsDiagnosticsItems',
      lookup: 'xrayLabsDiagnostics',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationLabDiagnostic === true,
    },
    {
      items: 'infoOtherHospitalizationItems',
      lookup: 'otherHospitalization',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationOtherRelated === true,
    },
    {
      items: 'infoMentalHealthPsychiatricItems',
      lookup: 'mentalHealthPsychiatric',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationMentalHealthPsychiatric === true,
    },
    {
      items: 'infoEmergencyAirTransportationItems',
      lookup: 'emergencyAirTransportation',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationEmergencyAirTransportation === true,
    },
    {
      items: 'infoAirfareReturnHomeItems',
      lookup: 'airfareReturnHome',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationAirfareReturnHome === true,
    },
    {
      items: 'infoFamilyTransportationItems',
      lookup: 'familyTransportation',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationFamilyTransportation === true,
    },
    {
      items: 'infoMedicalAttendantItems',
      lookup: 'medicalAttendant',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationMedicalAttendant === true,
    },

    // Essential Medical Appliances expenses
    {
      items: 'infoEssentialMedicalAppliancesItems',
      lookup: 'essentialMedicalAppliances',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES),
    },

    // Private Duty Nursing expenses
    {
      items: 'infoPrivateDutyNursingItems',
      lookup: 'privateDutyNursing',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING),
    },

    // Fracture Treatment expenses
    {
      items: 'infoFractureTreatmentItems',
      lookup: 'fractureTreatment',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.FRACTURE_TREATMENT),
    },

    // Maternity Benefits expenses
    {
      items: 'infoRoutinePreNatalCareItems',
      lookup: 'routinePreNatalCare',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS) && data.maternityBenefitsRoutinePreNatalCare === true,
    },
    {
      items: 'infoComplicationsOfPregnancyItems',
      lookup: 'complicationsOfPregnancy',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS) && data.maternityBenefitsComplicationsOfPregnancy === true,
    },
    {
      items: 'infoInvoluntaryTerminationItems',
      lookup: 'involuntaryTermination',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS) && data.maternityBenefitsInvoluntaryTermination === true,
    },

    // Professional Services expenses
    {
      items: 'infoPhysiotherapistItems',
      lookup: 'physiotherapist',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesPhysiotherapist === true,
    },
    {
      items: 'infoChiropractorItems',
      lookup: 'chiropractor',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesChiropractor === true,
    },
    {
      items: 'infoChiropodistItems',
      lookup: 'chiropodist',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesChiropodist === true,
    },
    {
      items: 'infoOsteopathItems',
      lookup: 'osteopath',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesOsteopath === true,
    },
    {
      items: 'infoPodiatristItems',
      lookup: 'podiatrist',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesPodiatrist === true,
    },
    {
      items: 'infoAcupuncturistItems',
      lookup: 'acupuncturist',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesAcupuncturist === true,
    },
    {
      items: 'infoNaturopathItems',
      lookup: 'naturopath',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesNaturopath === true,
    },
    {
      items: 'infoSpeechTherapistItems',
      lookup: 'speechTherapist',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesSpeechTherapist === true,
    },
    {
      items: 'infoMentalHealthOrSupportPractitionerItems',
      lookup: 'mentalHealthOrSupportPractitioner',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesMentalHealthOrSupportPractitioner === true,
    },

    // Rehabilitation Treatment expenses
    {
      items: 'infoRehabilitationTreatmentItems',
      lookup: 'rehabilitationTreatment',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.REHABILITATION_TREATMENT),
    },

    // Dental Services expenses
    {
      items: 'infoDentalAccidentItems',
      lookup: 'dentalAccident',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES) && data.dentalServicesDentalAccident === true,
    },
    {
      items: 'infoImpactedWisdomToothItems',
      lookup: 'impactedWisdomTooth',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES) && data.dentalServicesImpactedWisdomTooth === true,
    },
    {
      items: 'infoDentalPainOtherItems',
      lookup: 'dentalPainOther',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES) && data.dentalServicesDentalPainOther === true,
    },

    // Prescription Glasses, Contact Lenses or Hearing Aids expenses
    {
      items: 'infoPrescriptionGlassesContactLensesHearingAidsItems',
      lookup: 'prescriptionGlassesContactLensesHearingAids',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRESCRIPTION_GLASSES_CONTACT_LENSES_HEARING_AIDS),
    },

    // Tutorial Services expenses
    {
      items: 'infoTutorialServicesItems',
      lookup: 'tutorialServices',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.TUTORIAL_SERVICES),
    },
  ];
  // Map all expenses using the generic function
  const allExpenses = expenseConfigs.flatMap((config) =>
    mapExpensesByType(data, config.items, config.lookup, config.condition)
  );

  return [...allExpenses];
}

function mapToClaimExpensesLevel(data, claimTypes, selectedBenefits) {
  const selected =
    !!selectedBenefits && typeof selectedBenefits.toJS === 'function'
      ? selectedBenefits.toJS()
      : selectedBenefits || [];
  const claimCodeList = expenseLookup.claimCodeList;
  const list = [];
  if (selected.length > 0 && !selected.some((sel) => claimCodeList.includes(sel.claimCode))) {
    return list;
  }

  const expensesSelected = !!data.whatExpensesAreYouClaimingFor && typeof data.whatExpensesAreYouClaimingFor.toJS === 'function'
    ? data.whatExpensesAreYouClaimingFor.toJS()
    : data.whatExpensesAreYouClaimingFor || [];

  if (expensesSelected.length < 1 || claimTypes === undefined) {
    return list;
  }

  // Get all medical expenses
  const medicalExpenses = mapToExpenses(data);

  // Helper function to get claim code from lookup's associatedClaimCodes
  const getClaimCodeFromLookup = (lookupKey) => {
    const lookup = expenseLookup[lookupKey];
    return lookup && lookup.associatedClaimCodes && lookup.associatedClaimCodes.length > 0
      ? lookup.associatedClaimCodes[0]
      : null;
  };

  // Create mapping configuration by lookup keys (not hard-coded claim codes)
  // Note: Documents should ONLY be shown when specific questions are answered "Yes"
  // NOT when only the checkbox is selected
  const lookupKeyToConfig = {
    // Doctor's Visit - Annual Physical Visit
    annualPhysicalExam: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitAnnualPhysicalExam === 'true',
      getDescription: () => 'Annual Physical Visit',
    },
    // Doctor's Visit - First Visit
    outpatientPhysicianFees: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT, MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: (claimCode) => {
        // Check if this is from Doctor's Visit (First Visit)
        const isDoctorVisitFirst = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFirstVisit === 'true';
        // Check if this is from Hospitalization (Physician Invoice)
        const isHospitalizationPhysician = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationPhysicianInvoice === true;
        return isDoctorVisitFirst || isHospitalizationPhysician;
      },
      getDescription: () => {
        // If from Doctor's Visit First Visit, show "Physician fees"
        if (expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFirstVisit === 'true') {
          return 'Physician fees';
        }
        // If from Hospitalization Physician Invoice, show "Physician fees"
        if (expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationPhysicianInvoice === true) {
          return 'Physician fees';
        }
        return MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT;
      },
    },
    // Doctor's Visit - Follow-up Visit
    followUpVisit: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUp === 'true',
      getDescription: () => 'Follow-up visits',
    },
    // Fracture Treatment - Follow-up Fracture
    fractureTreatment: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT, MEDICAL_EXPENSE_TYPES.FRACTURE_TREATMENT],
      condition: () => {
        const isDoctorVisitFracture = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUpFracture === 'true';
        const isStandaloneFracture = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.FRACTURE_TREATMENT);
        return isDoctorVisitFracture || isStandaloneFracture;
      },
      getDescription: () => 'Fracture treatment',
    },
    // Doctor's Visit - STI Testing
    stiTesting: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitSTIVaccinationsTBEyeExam === 'true' && data.doctorVisitSTITesting === true,
      getDescription: () => 'Sexually Transmitted Infection Testing',
    },
    // Doctor's Visit - Vaccinations and Tuberculosis Testing
    vaccinationsTBTesting: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitSTIVaccinationsTBEyeExam === 'true' && data.doctorVisitVaccinationsTBTesting === true,
      getDescription: () => 'Vaccinations and Tuberculosis Testing',
    },
    // Doctor's Visit - Eye Examination
    eyeExamination: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitSTIVaccinationsTBEyeExam === 'true' && data.doctorVisitEyeExamination === true,
      getDescription: () => 'Eye Examination',
    },
    // Diagnostic Tests
    diagnosticTests: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS, MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: (claimCode) => {
        // Check if this is from Diagnostic Tests checkbox
        const isDiagnosticTests = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS);
        // Check if this is from Hospitalization (Lab/Diagnostic or Other Related)
        const isHospitalizationLab = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationLabDiagnostic === true;
        const isHospitalizationOther = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationOtherRelated === true;
        return isDiagnosticTests || isHospitalizationLab || isHospitalizationOther;
      },
      getDescription: () => {
        // If from Hospitalization, show "Diagnostic Tests"
        if (expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && 
            (data.hospitalizationLabDiagnostic === true || data.hospitalizationOtherRelated === true)) {
          return 'Diagnostic Tests';
        }
        // Otherwise use the expense type
        return MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS;
      },
    },
    // Prescription Drugs
    prescriptionDrugs: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS),
      getDescription: () => MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS,
    },
    // Ambulance Services - Ground
    groundAmbulance: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && (data.ambulanceGroundService === 'true' || data.ambulanceAirService === 'false'),
      getDescription: () => 'Ambulance services',
    },
    // Ambulance Services - Air
    airAmbulance: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceAirService === 'true',
      getDescription: () => 'Emergency Air Transportation',
    },
    // Hospitalization - Hospital Fees
    hospitalFees: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationHospitalInvoice === true,
      getDescription: () => 'Hospital fees',
    },
    // Hospitalization - Inpatient Physician Fees (uses same VTC-PHYS as outpatientPhysicianFees)
    inpatientPhysicianFees: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationPhysicianInvoice === true,
      getDescription: () => 'Physician fees',
    },
    // Hospitalization - X-ray/Labs/Diagnostics (uses same VTC-DIAG as diagnosticTests)
    xrayLabsDiagnostics: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationLabDiagnostic === true,
      getDescription: () => 'Diagnostic Tests',
    },
    // Hospitalization - Other Related (uses same VTC-DIAG as diagnosticTests)
    otherHospitalization: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationOtherRelated === true,
      getDescription: () => 'Diagnostic Tests',
    },
    // Hospitalization - Mental health or psychiatric hospitalization
    mentalHealthPsychiatric: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationMentalHealthPsychiatric === true,
      getDescription: () => 'Mental health or psychiatric hospitalization',
    },
    // Hospitalization - Emergency Air Transportation
    emergencyAirTransportation: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationEmergencyAirTransportation === true,
      getDescription: () => 'Emergency Air Transportation',
    },
    // Hospitalization - Airfare to Return Home For Treatment Benefit
    airfareReturnHome: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationAirfareReturnHome === true,
      getDescription: () => 'Airfare to Return Home For Treatment Benefit',
    },
    // Hospitalization - Family Transportation
    familyTransportation: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationFamilyTransportation === true,
      getDescription: () => 'Family Transportation',
    },
    // Hospitalization - Medical Attendant
    medicalAttendant: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationMedicalAttendant === true,
      getDescription: () => 'Medical Attendant',
    },
    // Essential Medical Appliances
    essentialMedicalAppliances: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES),
      getDescription: () => MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES,
    },
    // Private Duty Nursing
    privateDutyNursing: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING),
      getDescription: () => MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING,
    },
    // Maternity Benefits - Routine Pre-Natal Care
    routinePreNatalCare: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS) && data.maternityBenefitsRoutinePreNatalCare === true,
      getDescription: () => 'Routine Pre-Natal Care',
    },
    // Maternity Benefits - Complications of Pregnancy
    complicationsOfPregnancy: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS) && data.maternityBenefitsComplicationsOfPregnancy === true,
      getDescription: () => 'Complications of Pregnancy',
    },
    // Maternity Benefits - Involuntary termination
    involuntaryTermination: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS) && data.maternityBenefitsInvoluntaryTermination === true,
      getDescription: () => 'Involuntary termination',
    },
    // Professional Services - Physiotherapist
    physiotherapist: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesPhysiotherapist === true,
      getDescription: () => 'Physiotherapist',
    },
    // Professional Services - Chiropractor
    chiropractor: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesChiropractor === true,
      getDescription: () => 'Chiropractor',
    },
    // Professional Services - Chiropodist
    chiropodist: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesChiropodist === true,
      getDescription: () => 'Chiropodist',
    },
    // Professional Services - Osteopath
    osteopath: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesOsteopath === true,
      getDescription: () => 'Osteopath',
    },
    // Professional Services - Podiatrist
    podiatrist: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesPodiatrist === true,
      getDescription: () => 'Podiatrist',
    },
    // Professional Services - Acupuncturist
    acupuncturist: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesAcupuncturist === true,
      getDescription: () => 'Acupuncturist',
    },
    // Professional Services - Naturopath
    naturopath: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesNaturopath === true,
      getDescription: () => 'Naturopath',
    },
    // Professional Services - Speech Therapist
    speechTherapist: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesSpeechTherapist === true,
      getDescription: () => 'Speech Therapist',
    },
    // Professional Services - Mental Health or Support Practitioner
    mentalHealthOrSupportPractitioner: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesMentalHealthOrSupportPractitioner === true,
      getDescription: () => 'Mental Health or Support Practitioner',
    },
    // Rehabilitation Treatment
    rehabilitationTreatment: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.REHABILITATION_TREATMENT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.REHABILITATION_TREATMENT),
      getDescription: () => 'Rehabilitation Treatment',
    },
    // Dental Services - Dental Accident
    dentalAccident: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES) && data.dentalServicesDentalAccident === true,
      getDescription: () => 'Dental Accident (result of a blow to the face)',
    },
    // Dental Services - Impacted Wisdom Tooth
    impactedWisdomTooth: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES) && data.dentalServicesImpactedWisdomTooth === true,
      getDescription: () => 'Impacted Wisdom Tooth',
    },
    // Dental Services - Dental pain other than accident or wisdom tooth
    dentalPainOther: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES) && data.dentalServicesDentalPainOther === true,
      getDescription: () => 'Dental pain other than accident or wisdom tooth',
    },
    // Prescription Glasses, Contact Lenses or Hearing Aids
    prescriptionGlassesContactLensesHearingAids: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PRESCRIPTION_GLASSES_CONTACT_LENSES_HEARING_AIDS],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRESCRIPTION_GLASSES_CONTACT_LENSES_HEARING_AIDS),
      getDescription: () => 'Prescription Glasses, Contact Lenses or Hearing Aids',
    },
    // Tutorial Services
    tutorialServices: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.TUTORIAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.TUTORIAL_SERVICES),
      getDescription: () => 'Tutorial Services',
    },
  };

  // Build claimCodeToExpenseTypes dynamically from lookup keys
  const claimCodeToExpenseTypes = {};
  Object.keys(lookupKeyToConfig).forEach((lookupKey) => {
    const claimCode = getClaimCodeFromLookup(lookupKey);
    if (claimCode) {
      // If claim code already exists, merge the configs (for cases where multiple lookups share same claim code)
      if (claimCodeToExpenseTypes[claimCode]) {
        // Merge expense types
        claimCodeToExpenseTypes[claimCode].expenseTypes = [
          ...new Set([...claimCodeToExpenseTypes[claimCode].expenseTypes, ...lookupKeyToConfig[lookupKey].expenseTypes])
        ];
        // Merge conditions with OR logic
        const existingCondition = claimCodeToExpenseTypes[claimCode].condition;
        const newCondition = lookupKeyToConfig[lookupKey].condition;
        claimCodeToExpenseTypes[claimCode].condition = (claimCode) => 
          existingCondition(claimCode) || newCondition(claimCode);
        // Use the new description if it's more specific
        if (lookupKeyToConfig[lookupKey].getDescription) {
          claimCodeToExpenseTypes[claimCode].getDescription = lookupKeyToConfig[lookupKey].getDescription;
        }
      } else {
        claimCodeToExpenseTypes[claimCode] = { ...lookupKeyToConfig[lookupKey] };
      }
    }
  });

  // Map each claim type to its corresponding expenses
  claimTypes.forEach((item) => {
    const claimCodeConfig = claimCodeToExpenseTypes[item.claimCode];
    if (claimCodeConfig) {
      // Check if the specific condition is met (checkbox selected AND question answered "Yes")
      if (claimCodeConfig.condition(item.claimCode)) {
        // Find the matching expense type
        const matchingExpenseType = claimCodeConfig.expenseTypes.find((expenseType) =>
          expensesSelected.includes(expenseType)
        );
        if (matchingExpenseType) {
          // Get the appropriate description based on the specific condition
          const description = claimCodeConfig.getDescription 
            ? claimCodeConfig.getDescription() 
            : matchingExpenseType;
          const benefit = mapClaimExpensesLevelItem(
            item,
            description,
            medicalExpenses.filter((expense) => expense.claimTypeCode === item.claimCode)
          );
          list.push(benefit);
        }
      }
    }
  });

  return list;
}

function mapExpenseItem(item, itemDate, claimCode) {
  const selectedClaimForArray = (!Array.isArray(item.selectedClaimFor)) ? item.selectedClaimFor.toJS() : item.selectedClaimFor;
  const claimForInsuredIds = selectedClaimForArray.map((claimFor) => claimFor.policyRoleID);
  if (!item || !item.amountClaimed || !item.currency) {
    return null;
  }
  return {
    shortDescription: `${item.descriptionOfItem}`,
    dateOfExpense: item.dateOfExpenses,
    clientCost: item.amountClaimed,
    currency: item.currency,
    rate: null,
    requestedAmount: null,
    claimTypeCode: claimCode,
    claimForInsuredIds,
    claimForInsureds: selectedClaimForArray,
  };
}

function mapClaimExpensesLevelItem(claimType, description, expenses) {
  return {
    claimCode: claimType.claimCode,
    description,
    claimTypeId: claimType.claimTypeId,
    requiredDocuments: claimType.requiredDocuments,
    order: null,
    expenses: expenses || [],
  };
}

export default {
  mapToParentSection,
  mapToQuestionnaire,
  mapToExpenses,
  mapToClaimExpensesLevel,
};



// WEBPACK FOOTER //
// ./app/containers/FastCover/dataMapper/stepStudentMedicalBenefitsTugoDataMapper.js